import type { SubmissionState } from "../lib/labels";
import { submissionLabel } from "../lib/labels";

/**
 * Per-row submission indicator on the Syncs list. Reuses the existing
 * status-badge palette so the visual language matches StatusBadge /
 * UploadStatusBadge:
 *   - green badge-success : clean import (IMPORTED only)
 *   - yellow badge-warn   : Canvas accepted but flagged warnings, OR partial failure
 *   - red badge-failed    : Canvas rejected the import OR auto-submit Lambda crashed
 *   - blue badge-running  : in flight (with pulse)
 *   - gray badge-queued   : pending (with pulse)
 *   - muted badge-cancelled : off / unknown
 *
 * IMPORTED_WITH_MESSAGES and FAILED_WITH_MESSAGES both render yellow but
 * with distinct text — "Submitted (warnings)" vs "Partially failed" — so
 * the user can tell them apart at a glance.
 */
const variants: Record<
  SubmissionState,
  { className: string; pulse: boolean; icon: string }
> = {
  submitted: { className: "badge badge-success", pulse: false, icon: "" },
  submittedWithWarnings: {
    className: "badge badge-warn",
    pulse: false,
    icon: "",
  },
  submittedPartial: { className: "badge badge-warn", pulse: false, icon: "" },
  submitFailed: { className: "badge badge-failed", pulse: false, icon: "" },
  autoSubmitError: { className: "badge badge-failed", pulse: false, icon: "" },
  submitting: { className: "badge badge-running", pulse: true, icon: "" },
  shippedViaBundle: {
    className: "badge badge-success",
    pulse: false,
    icon: "",
  },
  viaBundle: { className: "badge badge-queued", pulse: false, icon: "" },
  pending: { className: "badge badge-queued", pulse: true, icon: "" },
  nothingToSubmit: {
    className: "badge badge-cancelled",
    pulse: false,
    icon: "",
  },
  off: { className: "badge badge-cancelled", pulse: false, icon: "" },
  unknown: { className: "badge badge-cancelled", pulse: false, icon: "" },
};

export function SubmissionBadge({ state }: { state: SubmissionState }) {
  const v = variants[state];
  return (
    <span className={v.className}>
      {v.pulse && <span className="badge-pulse" />}
      {v.icon && <>{v.icon} </>}
      {submissionLabel(state)}
    </span>
  );
}
